// ── Geometry utilities for the Lacuna board ──

import type { Vertex, BoardState } from '../game/state';

export const BOARD_RADIUS = 3;
export const ANCHOR_COUNT = 12;

/** Angle (radians) of anchor i (1-indexed) on the circle. Anchor 1 is at top. */
export function anchorAngle(i: number): number {
  return Math.PI / 2 - ((i - 1) * 2 * Math.PI) / ANCHOR_COUNT;
}

/** Position of anchor i on the circle */
export function anchorPosition(i: number): { x: number; y: number } {
  const a = anchorAngle(i);
  return { x: BOARD_RADIUS * Math.cos(a), y: BOARD_RADIUS * Math.sin(a) };
}

/** Build the 12 anchor vertices */
export function createAnchors(): Record<string, Vertex> {
  const anchors: Record<string, Vertex> = {};
  for (let i = 1; i <= ANCHOR_COUNT; i++) {
    const pos = anchorPosition(i);
    anchors[`A${i}`] = {
      id: `A${i}`,
      type: 'anchor',
      x: pos.x,
      y: pos.y,
      sockets: 2,
      free: 2,
    };
  }
  return anchors;
}

/** Next anchor clockwise (wrapping) */
export function nextAnchorCW(i: number): number {
  return i === ANCHOR_COUNT ? 1 : i + 1;
}

/** Next anchor counter-clockwise (wrapping) */
export function nextAnchorCCW(i: number): number {
  return i === 1 ? ANCHOR_COUNT : i - 1;
}

/** Anchor index from vertex id (e.g. "A3" → 3). Returns 0 if not an anchor. */
export function anchorIndex(id: string): number {
  if (!id.startsWith('A')) return 0;
  return parseInt(id.slice(1), 10);
}

/** Distance between two points */
export function dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** Angle from point a to point b (radians, standard math convention) */
export function angleTo(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.atan2(b.y - a.y, b.x - a.x);
}

/** Normalize angle to [0, 2π) */
export function normalizeAngle(a: number): number {
  const TWO_PI = 2 * Math.PI;
  return ((a % TWO_PI) + TWO_PI) % TWO_PI;
}

/** Check if a point is inside the outer circle */
export function isInsideCircle(x: number, y: number): boolean {
  return x * x + y * y < BOARD_RADIUS * BOARD_RADIUS;
}

// ── Topology-guided strand routing ──
//
// Each strand borders exactly two faces in the planar graph. We curve
// the strand toward the interior of the smaller/more-interior face.
// Since faces partition the plane and don't overlap, strands routed
// into different faces can NEVER cross. This is the only correct
// approach for guaranteed non-crossing curves in a planar graph
// with arbitrary vertex positions.

const DEFAULT_BULGE = 0.04; // subtle default arc when no face info

/**
 * Compute SVG path for a strand, curving toward a given face centroid.
 * If no centroid is given, applies a subtle default outward arc.
 *
 * The curve is a single cubic Bezier: control points are offset from
 * the 1/3 and 2/3 points along the straight line, perpendicular toward
 * the face centroid.
 */
export function strandPathWithFace(
  from: { x: number; y: number },
  to: { x: number; y: number },
  faceCentroid: { x: number; y: number } | null,
): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);

  if (len < 0.001) {
    return `M ${from.x} ${-from.y} L ${to.x} ${-to.y}`;
  }

  // Perpendicular unit vector
  const nx = -dy / len;
  const ny = dx / len;

  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;

  let offsetX: number;
  let offsetY: number;

  if (faceCentroid) {
    // Direction from strand midpoint toward face centroid
    const toCx = faceCentroid.x - mx;
    const toCy = faceCentroid.y - my;

    // Project onto the perpendicular direction
    const perpProj = toCx * nx + toCy * ny;
    const sign = perpProj >= 0 ? 1 : -1;

    // Offset amount: proportional to distance, moderate curve
    const amount = Math.min(len * 0.18, Math.abs(perpProj) * 0.4, 0.5) * sign;
    offsetX = nx * amount;
    offsetY = ny * amount;
  } else {
    // Default: subtle outward arc (away from board center)
    const dot = mx * nx + my * ny;
    const sign = dot >= 0 ? 1 : -1;
    offsetX = nx * len * DEFAULT_BULGE * sign;
    offsetY = ny * len * DEFAULT_BULGE * sign;
  }

  const cx1 = from.x + dx * 0.33 + offsetX;
  const cy1 = from.y + dy * 0.33 + offsetY;
  const cx2 = from.x + dx * 0.67 + offsetX;
  const cy2 = from.y + dy * 0.67 + offsetY;

  return `M ${from.x} ${-from.y} C ${cx1} ${-cy1}, ${cx2} ${-cy2}, ${to.x} ${-to.y}`;
}

/**
 * Simple strand routing (no topology info). Returns waypoints for compatibility.
 * Used by rules page and previews where face info isn't available.
 */
export function routeStrand(
  from: { x: number; y: number },
  to: { x: number; y: number },
  _obstacles: Array<{ x: number; y: number }> = [],
): Array<{ x: number; y: number }> {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);

  if (len < 0.01) return [from, to];

  const nx = -dy / len;
  const ny = dx / len;
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const dot = mx * nx + my * ny;
  const sign = dot >= 0 ? 1 : -1;
  const bulge = DEFAULT_BULGE * len * sign;

  return [from, { x: mx + nx * bulge, y: my + ny * bulge }, to];
}

/**
 * Convert an array of waypoints to a smooth SVG path using
 * Catmull-Rom → cubic Bezier conversion.
 * Y coordinates are negated for SVG.
 */
export function waypointsToSVGPath(
  pts: Array<{ x: number; y: number }>,
): string {
  if (pts.length < 2) return '';
  if (pts.length === 2) {
    return `M ${pts[0].x} ${-pts[0].y} L ${pts[1].x} ${-pts[1].y}`;
  }

  const parts: string[] = [`M ${pts[0].x} ${-pts[0].y}`];

  // Catmull-Rom to cubic Bezier, tension = 0 (uniform)
  // For each segment i → i+1, we need points i-1, i, i+1, i+2
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];

    // Catmull-Rom → Bezier control points
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    parts.push(`C ${cp1x} ${-cp1y}, ${cp2x} ${-cp2y}, ${p2.x} ${-p2.y}`);
  }

  return parts.join(' ');
}

/**
 * Compute routed SVG path data for a strand.
 * Uses force-directed routing + Catmull-Rom smoothing.
 */
export function strandPath(
  from: { x: number; y: number },
  to: { x: number; y: number },
  obstacles: Array<{ x: number; y: number }> = [],
): string {
  const waypoints = routeStrand(from, to, obstacles);
  return waypointsToSVGPath(waypoints);
}

/**
 * Legacy compat: returns cubic Bezier control points for a single-segment strand.
 * Used by the lacuna fill path builder.
 */
export function strandControlPoints(
  from: { x: number; y: number },
  to: { x: number; y: number },
  obstacles: Array<{ x: number; y: number }> = [],
): { cx1: number; cy1: number; cx2: number; cy2: number } {
  const waypoints = routeStrand(from, to, obstacles);

  // Fit a single cubic Bezier to the routed waypoints
  // Use 1/3 and 2/3 waypoints as approximation of control points
  const n = waypoints.length;
  if (n <= 2) {
    const mx = (from.x + to.x) / 2;
    const my = (from.y + to.y) / 2;
    return { cx1: mx, cy1: my, cx2: mx, cy2: my };
  }

  const i1 = Math.round((n - 1) * 0.33);
  const i2 = Math.round((n - 1) * 0.67);

  return {
    cx1: waypoints[i1].x,
    cy1: waypoints[i1].y,
    cx2: waypoints[i2].x,
    cy2: waypoints[i2].y,
  };
}

/** SVG arc path between two anchors (along the outer circle).
 *  We use small arcs (< 180°) between adjacent anchors.
 *  Note: SVG y is flipped (negative). */
export function arcPath(
  from: { x: number; y: number },
  to: { x: number; y: number },
): string {
  // Always use the short arc (sweep flag depends on direction)
  return `M ${from.x} ${-from.y} A ${BOARD_RADIUS} ${BOARD_RADIUS} 0 0 1 ${to.x} ${-to.y}`;
}

/**
 * Departure angle of the arc edge from anchor i toward anchor j.
 * j must be adjacent to i (i.e., j = i±1 mod 12).
 */
export function arcDepartureAngle(fromIdx: number, toIdx: number): number {
  const theta = anchorAngle(fromIdx);
  // CW neighbor: departure = theta - π/2
  // CCW neighbor: departure = theta + π/2
  const cw = nextAnchorCW(fromIdx);
  if (toIdx === cw) {
    return normalizeAngle(theta - Math.PI / 2);
  }
  return normalizeAngle(theta + Math.PI / 2);
}

/**
 * Check if two line segments (a1-a2) and (b1-b2) intersect.
 * Returns true if they cross (not just touch at endpoints).
 */
export function segmentsIntersect(
  a1: { x: number; y: number },
  a2: { x: number; y: number },
  b1: { x: number; y: number },
  b2: { x: number; y: number },
  epsilon = 1e-9,
): boolean {
  const d1x = a2.x - a1.x;
  const d1y = a2.y - a1.y;
  const d2x = b2.x - b1.x;
  const d2y = b2.y - b1.y;

  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < epsilon) return false; // parallel

  const dx = b1.x - a1.x;
  const dy = b1.y - a1.y;
  const t = (dx * d2y - dy * d2x) / cross;
  const u = (dx * d1y - dy * d1x) / cross;

  // Strict interior intersection (exclude endpoints)
  return t > epsilon && t < 1 - epsilon && u > epsilon && u < 1 - epsilon;
}

/**
 * Point-in-polygon test using winding number.
 * Polygon is an array of {x, y} forming a closed loop.
 */
export function pointInPolygon(
  px: number,
  py: number,
  polygon: Array<{ x: number; y: number }>,
): boolean {
  let winding = 0;
  const n = polygon.length;

  for (let i = 0; i < n; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % n];

    if (a.y <= py) {
      if (b.y > py) {
        // Upward crossing
        const cross = (b.x - a.x) * (py - a.y) - (px - a.x) * (b.y - a.y);
        if (cross > 0) winding++;
      }
    } else {
      if (b.y <= py) {
        // Downward crossing
        const cross = (b.x - a.x) * (py - a.y) - (px - a.x) * (b.y - a.y);
        if (cross < 0) winding--;
      }
    }
  }

  return winding !== 0;
}

/**
 * Approximate an arc on the circle between two anchor positions
 * as a polyline for point-in-polygon testing.
 */
export function arcPolyline(
  fromAngle: number,
  toAngle: number,
  steps = 8,
): Array<{ x: number; y: number }> {
  const points: Array<{ x: number; y: number }> = [];

  // Normalize: go clockwise from fromAngle to toAngle
  let start = normalizeAngle(fromAngle);
  let end = normalizeAngle(toAngle);

  // Clockwise means decreasing angle
  let delta = start - end;
  if (delta < 0) delta += 2 * Math.PI;
  if (delta > Math.PI) {
    // Go CCW instead
    delta = -(2 * Math.PI - delta);
  }

  for (let s = 0; s <= steps; s++) {
    const t = s / steps;
    const angle = start - delta * t;
    points.push({
      x: BOARD_RADIUS * Math.cos(angle),
      y: BOARD_RADIUS * Math.sin(angle),
    });
  }
  return points;
}

/** Chaikin's corner-cutting smoothing. Preserves endpoints. */
export function chaikinSmooth(
  pts: Array<{ x: number; y: number }>,
  iterations = 2,
): Array<{ x: number; y: number }> {
  if (pts.length <= 2) return pts;
  let current = pts;
  for (let iter = 0; iter < iterations; iter++) {
    const next: Array<{ x: number; y: number }> = [current[0]];
    for (let i = 0; i < current.length - 1; i++) {
      const a = current[i];
      const b = current[i + 1];
      if (i > 0) {
        next.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
      }
      if (i < current.length - 2) {
        next.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
      }
    }
    next.push(current[current.length - 1]);
    current = next;
  }
  return current;
}
